import { ApiQuestCostEstimatePayload, ApiQuestDetail } from "griptonite-api-client/types";
import isEqual from "lodash/isEqual";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useDebounce } from "use-debounce";

import { useQuestCostEstimateQuery } from "@/hooks/quests";
import { useVenueQuery } from "@/hooks/venues";

import { QuestRulesFormValues, questRulesFormValuesToEstimateLists } from "./rulesForms";

// Long enough to wait out typing in a number field, short enough to feel live.
const DEBOUNCE_MS = 500;

/**
 * The cost estimate for the Rules tab's form as it stands. The month and disciplines come from the
 * saved quest, since they're edited on the Profile tab. Every caller asks for the same estimate, so
 * they share one request.
 */
export default function useQuestRulesCostEstimate(quest: ApiQuestDetail) {
  const { data: venue } = useVenueQuery(quest.venue.uuid);
  const values = useWatch<QuestRulesFormValues>() as QuestRulesFormValues;

  // A quest with no disciplines saved yet will count every one the venue offers.
  const climbTypes = quest.climbTypes?.length ? quest.climbTypes : venue?.climbTypes;
  const payload = useMemo<ApiQuestCostEstimatePayload | undefined>(
    () =>
      quest.startTime && climbTypes?.length
        ? {
            venue: quest.venue.uuid,
            startTime: quest.startTime,
            climbTypes,
            ...questRulesFormValuesToEstimateLists(values, quest),
          }
        : undefined,
    [quest, climbTypes, values],
  );
  const [debouncedPayload] = useDebounce(payload, DEBOUNCE_MS, { equalityFn: isEqual });
  return useQuestCostEstimateQuery(debouncedPayload);
}
